import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import { ref } from 'vue';

const ACCESS_TOKEN_KEY = 'wiservet:access_token';

// Ref REACTIVO del access token. Los computed de Pinia que decodifican el JWT
// (isImpersonating, impersonatedByAdmin, clinicRole, canViewFinancials…) deben
// re-evaluarse cuando el token cambia en runtime (ej. tras impersonar sin
// recargar la página). Leer sessionStorage directo NO es reactivo → el banner de
// impersonación no aparecía sin refresh manual (cto-review de 576ad44) y ya había
// causado un bug de la pestaña Finanzas (2026-06-17). accessTokenRef es la ÚNICA
// fuente de verdad en runtime; sessionStorage solo persiste entre recargas.
const accessTokenRef = ref<string | null>(sessionStorage.getItem(ACCESS_TOKEN_KEY));

let refreshPromise: Promise<string | null> | null = null;
let onUnauthorized: (() => void) | null = null;

export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export function getAccessToken(): string | null {
  return accessTokenRef.value;
}

export function setAccessToken(token: string | null) {
  accessTokenRef.value = token;
  if (token) sessionStorage.setItem(ACCESS_TOKEN_KEY, token);
  else sessionStorage.removeItem(ACCESS_TOKEN_KEY);
}

interface RetryConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
}

export const api: AxiosInstance = axios.create({
  baseURL: '/api/v1',
  withCredentials: true,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && config.headers) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const resp = await axios.post(
        '/api/v1/auth/refresh',
        {},
        { withCredentials: true, timeout: 10000 },
      );
      const newToken = resp.data?.access_token as string | undefined;
      if (newToken) {
        setAccessToken(newToken);
        return newToken;
      }
      return null;
    } catch {
      return null;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

// D75+1: dispara logout real (evento global, sin importar el router acá —
// evitaría un ciclo api.ts→router→stores/auth→api.ts) cuando un 401 no se
// puede resolver con refresh — token realmente inválido/vencido. Antes esto
// caía silencioso dejando al usuario "colgado" en la misma pantalla sin
// redirect (solo router.beforeEach corría, y solo en cambio de ruta).
// Escuchado por router/index.ts (redirect a /login).
//
// HOTFIX 2026-07-07: originalmente esta función también se disparaba con
// cualquier 400 NO_CLINIC_CONTEXT — pero ese código NO siempre significa
// sesión rota. Un superadmin Wiservet recién logueado, SIN clínica elegida
// todavía, también recibe NO_CLINIC_CONTEXT en la primera carga del
// dashboard — es un estado normal (tiene que usar el selector de clínica),
// no una sesión expirada. Tratarlo como logout forzado deslogueaba al CEO
// en <1s tras cada login. La causa raíz real de D75+1 bug 3 (impersonación
// perdía clinic_id en el refresh) ya se arregló en el backend
// (Impersonate()/Refresh(), ver migración 0088) — no hace falta este
// catch-all agresivo en el frontend.
function forceSessionExpired() {
  setAccessToken(null);
  if (onUnauthorized) onUnauthorized();
  window.dispatchEvent(new CustomEvent('wiservet:session-expired'));
}

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    if (error.response?.status === 401 && original && !original._retried) {
      const url = original.url ?? '';
      if (url.includes('/auth/login') || url.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      original._retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        if (original.headers) original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      }
      forceSessionExpired();
    }
    // S2.7 D55-c: 402 Payment Required cuando el backend bloquea writes por
    // trial vencido / cobro fallido. Emitimos un CustomEvent global que el
    // componente TrialReadonlyOverlay (montado en AppShell) escucha para
    // mostrar el modal "Tu trial venció". NO redirigimos automáticamente —
    // el user decide cuándo ir a billing.
    if (error.response?.status === 402) {
      const data = error.response.data as {
        error?: string;
        code?: string;
        subscription_status?: string;
        days_overdue?: number;
        redirect_url?: string;
      } | undefined;
      window.dispatchEvent(
        new CustomEvent('wiservet:trial-expired', {
          detail: {
            message: data?.error ?? 'Tu trial venció. Suscríbete para continuar.',
            code: data?.code ?? 'TRIAL_EXPIRED',
            subscriptionStatus: data?.subscription_status ?? 'demo',
            daysOverdue: data?.days_overdue ?? 0,
            redirectUrl: data?.redirect_url ?? '/app/config?subscribe=1',
          },
        }),
      );
    }
    // Plan v3 permisos por rol: 403 del gate granular (rol sin el permiso en la
    // matriz de la clínica). SOLO code=INSUFFICIENT_PERMISSION — cualquier otro
    // 403 (rol legacy, sin clínica, etc.) sigue exactamente como hoy. Emitimos
    // un evento global que AccessDeniedNotice (montado en AppShell) muestra como
    // aviso deduplicado por pantalla. NO redirige ni rompe la vista.
    if (isAccessDenied(error)) {
      window.dispatchEvent(
        new CustomEvent<AccessDeniedDetail>(ACCESS_DENIED_EVENT, {
          detail: {
            url: error.config?.url ?? '',
            method: (error.config?.method ?? 'get').toUpperCase(),
          },
        }),
      );
    }
    return Promise.reject(error);
  },
);

/** Evento global de acceso denegado por rol (403 INSUFFICIENT_PERMISSION). */
export const ACCESS_DENIED_EVENT = 'wiservet:access-denied';

export interface AccessDeniedDetail {
  /** URL relativa del request denegado (sin host). */
  url: string;
  method: string;
}

/**
 * true si el error es un 403 del gate de permisos por rol. Las vistas lo usan
 * para distinguir "sin acceso" de "vacío" (no mostrar "no hay datos" cuando en
 * realidad el rol no puede verlos).
 */
export function isAccessDenied(err: unknown): boolean {
  const ax = err as AxiosError<{ code?: string }> | undefined;
  return ax?.response?.status === 403 && ax.response?.data?.code === 'INSUFFICIENT_PERMISSION';
}

export interface ApiError {
  code: string;
  message: string;
  status: number;
}

export function asApiError(err: unknown): ApiError {
  const ax = err as AxiosError<{ code?: string; error?: string }>;
  const status = ax.response?.status ?? 0;
  const code = ax.response?.data?.code ?? 'NETWORK_ERROR';
  const message = ax.response?.data?.error ?? ax.message ?? 'Error de red';
  return { code, message, status };
}
